import { CaretRight } from "@phosphor-icons/react";
import clsx from "clsx";

import QuantitySelector from "common/components/subscriptions/QuantitySelector";
import { Button } from "common/components/ui/Button";
import { DialogFooter, DialogTitle } from "common/components/ui/Dialog";
import { DrawerFooter, DrawerTitle } from "common/components/ui/Drawer";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { PricingPeriodUnit } from "common/types";

import SubscriptionPlan from "../SubscriptionPlan";
import PricingInfo from "./PricingInfo";

const planOptions: {
  value: PricingPeriodUnit;
  label: string;
}[] = [
  {
    value: "month",
    label: "Monthly",
  },
  {
    value: "year",
    label: "Yearly",
  },
];

interface ManageSeatsProps {
  licenseCount: number;
  setLicenseCount: (newCount: number) => void;
  selectedPeriodUnit: PricingPeriodUnit;
  setSelectedPeriodUnit: (newPeriodUnit: PricingPeriodUnit) => void;
  goToNextStep: () => void;
}

const MAX_SEATS = 9999;

export default function ManageSeats({
  licenseCount,
  setLicenseCount,
  selectedPeriodUnit,
  setSelectedPeriodUnit,
  goToNextStep,
}: ManageSeatsProps) {
  const isDesktop = useTwBreakpoint("lg");
  const Footer = isDesktop ? DialogFooter : DrawerFooter;

  const {
    subscription,
    license_count: workspaceLicenseCount,
    lifetime_license_count: lifetimeLicenseCount,
  } = useSelectedWorkspaceContext();

  const isLifetimeDeal = lifetimeLicenseCount > 0;

  const isYearlySubscription =
    subscription && subscription.item_price.period_unit === "year";

  let isNextButtonDisabled = licenseCount === 0;

  if (!subscription && isLifetimeDeal) {
    isNextButtonDisabled = licenseCount === workspaceLicenseCount;
  } else if (subscription) {
    isNextButtonDisabled =
      licenseCount === workspaceLicenseCount &&
      selectedPeriodUnit === subscription.item_price.period_unit;
  }

  const minValue = subscription ? 0 : 1;

  const Title = isDesktop ? DialogTitle : DrawerTitle;

  return (
    <>
      <Title className="mb-8">Manage subscription</Title>

      <div
        className={clsx(
          "mx-auto flex w-[224px] items-center gap-x-1 rounded-full p-1 mb-4",
          isYearlySubscription ? "bg-black-200" : "bg-black-100",
        )}
      >
        {planOptions.map(({ value, label }) => {
          const isSelected = value === selectedPeriodUnit;

          if (isYearlySubscription && value === "month") {
            return (
              <Tooltip key={value}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost-black"
                    size="sm"
                    className="flex-1"
                    disabled
                  >
                    {label}
                  </Button>
                </TooltipTrigger>

                <TooltipContent side="bottom">
                  If you want to switch from a yearly to monthly subscription,
                  contact our support team through the live chat
                </TooltipContent>
              </Tooltip>
            );
          }

          return (
            <Button
              onClick={() => setSelectedPeriodUnit(value)}
              key={value}
              variant={isSelected ? "default-black" : "ghost-black"}
              size="sm"
              className="flex-1"
            >
              {label}
            </Button>
          );
        })}
      </div>

      <SubscriptionPlan plan="outreach" />

      <QuantitySelector
        quantity={licenseCount}
        setQuantity={setLicenseCount}
        minValue={
          isLifetimeDeal ? Math.max(minValue, lifetimeLicenseCount) : minValue
        }
        maxValue={MAX_SEATS}
      />
      <div className="flex flex-col gap-y-2.5">
        <PricingInfo
          currentCount={workspaceLicenseCount}
          subscription={subscription}
          newCount={licenseCount}
          selectedPeriodUnit={selectedPeriodUnit}
          type="user"
        />
      </div>

      <Footer className="mt-6">
        <Button
          rightIcon={<CaretRight />}
          size={isDesktop ? "md" : "lg"}
          className="ml-auto"
          onClick={goToNextStep}
          disabled={isNextButtonDisabled}
        >
          Next
        </Button>
      </Footer>
    </>
  );
}
